// frontend/book-app-frontend/src/components/MyComments.js
// Versão específica e simplificada para o usuário do tipo READER.

import { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { Card, ListGroup, Button, Badge, Spinner, Form, Row, Col } from 'react-bootstrap';
import { useAlertContext } from '../../contexts/AlertContext';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import translations from './translations';
import PaginationComponent from '../PaginationComponent';
import useMediaQuery from '../../utils/useMediaQuery';
import { FaComments } from 'react-icons/fa';


function MyComments() {
  // Estados principais
  const [allComments, setAllComments] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [commentsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalComments, setTotalComments] = useState(0);
  const indexOfLastComment = currentPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Estados para os filtros
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Contextos
  const { isLoggedIn } = useContext(AuthContext); // Não precisamos mais da userRole aqui
  const { language } = useContext(LanguageContext);
  const { showError } = useAlertContext();

  const t = translations[language] || translations.pt;

  // Função para buscar os comentários do usuário logado
  const fetchMyComments = useCallback(async () => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const auth = localStorage.getItem('auth');
      // O backend já está configurado para retornar apenas os comentários do READER
      // quando um usuário com essa role faz a chamada.
      const response = await axios.get(API_ENDPOINTS.COMMENTS_ALL, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      const fetchedComments = response.data;
      const sortedComments = fetchedComments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setAllComments(sortedComments);
    } catch (error) {
      showError(`${t.failedToFetchComments}: ${error.message}`);
      setAllComments([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, t.failedToFetchComments, showError]);

  // Efeito para buscar comentários quando o componente monta
  useEffect(() => {
    fetchMyComments();
  }, [fetchMyComments]);

  // Efeito para aplicar filtros e paginação
  useEffect(() => {
    let filteredComments = [...allComments];

    if (startDate) {
      const start = new Date(startDate);
      filteredComments = filteredComments.filter(comment => new Date(comment.createdAt) >= start);
    }

    if (endDate) {
      const end = new Date(endDate + 'T23:59:59.999Z');
      filteredComments = filteredComments.filter(comment => new Date(comment.createdAt) <= end);
    }

    setTotalComments(filteredComments.length);
    setTotalPages(Math.ceil(filteredComments.length / commentsPerPage));

    const startIndex = (currentPage - 1) * commentsPerPage;
    const endIndex = startIndex + commentsPerPage;
    setComments(filteredComments.slice(startIndex, endIndex));

  }, [allComments, currentPage, commentsPerPage, startDate, endDate]);

  // Função para limpar os filtros
  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  // Função para mudar de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2">
          <div className="d-inline-flex align-items-center gap-2"><FaComments className="text-primary" /> {t.myComments}</div>
        </Card.Header>

        <Card.Body className="border-bottom">
          <h5 className="mb-3">{t.filters}</h5>
          <Form>
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t.startDate}</Form.Label>
                  <Form.Control type="date" value={startDate} onChange={e => {
                    setStartDate(e.target.value)
                    setCurrentPage(1);
                  }} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t.endDate}</Form.Label>
                  <Form.Control type="date" value={endDate} onChange={e => {
                    setEndDate(e.target.value)
                    setCurrentPage(1);
                  }} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button variant="secondary" onClick={handleClearFilters} className="w-100">
                  {t.clearFilters}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>

        <Card.Body>
          {loading ? (
            <div className="text-center py-5 d-flex align-items-center justify-content-center">
              <Spinner animation="border" role="status" className="me-2" />
              <span>{t.loadingComments}</span>
            </div>
          ) : (
            <>
              {comments.length > 0 ? (
                <ListGroup variant="flush">
                  {comments.map(comment => (
                    <ListGroup.Item key={comment.id} className="border-bottom py-3">
                      {/* O botão de deletar foi removido daqui */}
                      <CommentDetails comment={comment} t={t} />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-center text-muted py-4">
                  {allComments.length > 0 ? 'Nenhum comentário corresponde aos filtros aplicados.' : t.noCommentsFound}
                </div>
              )}
              <PaginationComponent
                totalPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={handlePageChange}
                t={t}
                lengthTotal={totalComments}
                tItemName={t.commentsLow}
                indexOfFirst={indexOfFirstComment}
                indexOfLast={indexOfLastComment}
                isMobile={isMobile}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

// Componente CommentDetails permanece o mesmo, pois é apenas para exibição.
const CommentDetails = ({ comment, t }) => {
  return (
    <div>
      <div className="mb-2">
        <strong>"{comment.text}"</strong>
      </div>
      <div className="d-flex gap-2 mb-2">
        {comment.pageNumber && (
          <Badge bg="secondary" className="align-content-center">{t.pageNumber}: {comment.pageNumber}</Badge>
        )}
        {comment.sugestionType && (
          <Badge bg="info" pill="true" className="align-content-center">{comment.sugestionType}</Badge>
        )}
      </div>
      <div className="text-muted small mb-2">
        <strong>{t.createdBy}:</strong> {comment.creator?.name || comment.creator?.username} - {new Date(comment.createdAt).toLocaleString()}
      </div>
      {comment.chapter && (
        <div className="text-muted small">
          <strong>{t.chapter}:</strong> {comment.chapter.title}
          {comment.chapter.book && (
            <span> (${comment.chapter.book.title})</span>
          )}
        </div>
      )}
      {comment.imageBase64 && comment.nameImageBase64 && (
        <div className="mt-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              const link = document.createElement("a");
              link.href = `data:application/octet-stream;base64,${comment.imageBase64}`;
              link.download = comment.nameImageBase64;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            {t.download}: "{comment.nameImageBase64}"
          </Button>
        </div>
      )}
    </div>
  );
};

export default MyComments;
